<script setup lang="ts">
import ReviewRow from "./ReviewRow.vue";
import type { PendingBoardColumn } from "../api";

// Dumb by design: the backend decides which columns exist, their order, and
// which reviews sit in each (see design/actions.md § Pending-response board).
// This component only lays them out and forwards clicks.
defineProps<{ columns: PendingBoardColumn[] }>();

const emit = defineEmits<{ (e: "open", reviewId: number): void }>();

function devName(email: string): string {
  return email.split("@")[0];
}
</script>

<template>
  <div class="board" data-testid="pending-response-board">
    <section
      v-for="col in columns"
      :key="col.dev"
      class="board-col"
      :class="{ 'is-empty': col.pending_count === 0 }"
      :data-dev="col.dev"
    >
      <header class="col-head">
        <span class="col-dev" :title="col.dev">{{ devName(col.dev) }}</span>
        <span
          class="col-count"
          :class="{ zero: col.pending_count === 0 }"
          :data-testid="'col-count-' + col.dev"
        >
          {{ col.pending_count }}
        </span>
      </header>

      <div v-if="col.reviews.length" class="col-body">
        <ReviewRow
          v-for="r in col.reviews"
          :key="r.id"
          :review="r"
          compact
          @open="(id: number) => emit('open', id)"
        />
      </div>
      <p v-else class="col-empty">No pending responses.</p>
    </section>
  </div>
</template>

<style scoped>
/* Horizontal Kanban: columns scroll sideways when they overflow the page,
   so the board never forces the page body to scroll horizontally. */
.board {
  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: minmax(240px, 1fr);
  gap: 12px;
  overflow-x: auto;
  padding-bottom: 8px;
  align-items: start;
}
.board-col {
  display: flex;
  flex-direction: column;
  gap: 8px;
  background: var(--p-surface-50, #f8fafc);
  border: 1px solid var(--p-surface-200);
  border-radius: 8px;
  padding: 10px;
  min-height: 96px;
}
.board-col.is-empty {
  background: repeating-linear-gradient(
    45deg,
    var(--p-surface-50, #f8fafc),
    var(--p-surface-50, #f8fafc) 10px,
    var(--p-surface-100, #f1f5f9) 10px,
    var(--p-surface-100, #f1f5f9) 20px
  );
}
.col-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 2px 4px 6px;
  border-bottom: 1px solid var(--p-surface-200);
}
.col-dev {
  font-weight: 600;
  font-size: 13px;
  color: var(--p-surface-800);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.col-count {
  flex-shrink: 0;
  min-width: 20px;
  text-align: center;
  font-size: 11px;
  font-weight: 700;
  color: white;
  background: var(--p-primary-500);
  border-radius: 10px;
  padding: 1px 7px;
}
.col-count.zero {
  color: var(--p-surface-500);
  background: var(--p-surface-200);
}
.col-body {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
/* Inside a narrow column the row's own top margin (for stacked rows in the
   main lists) would double the gap — the flex `gap` already spaces them. */
.col-body :deep(.review-row) {
  margin-top: 0;
}
.col-empty {
  margin: 8px 4px;
  font-size: 12px;
  color: var(--p-surface-500);
  font-style: italic;
}
</style>
